import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const isCourse = new URLSearchParams(window.location.search).get("type") === "course";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) {
        setStatus("timeout");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch (e) {
        console.error("Payment status poll failed", e);
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-[#121721] border border-white/10 p-8 text-center space-y-5">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-amber-400 mx-auto animate-spin" />
            <h1 className="text-xl font-bold text-white">Confirming your payment…</h1>
            <p className="text-slate-400 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-amber-400 mx-auto" />
            <h1 className="text-2xl font-bold text-white">You're in!</h1>
            <p className="text-slate-400 text-sm">
              {isCourse
                ? "You're enrolled — the instructor keeps 98% and the flat 2% platform fee was applied transparently."
                : "Your investment is funded and the deal owner has been notified. The 2% platform fee was applied transparently."}
            </p>
            <Button data-testid="success-my-deals" onClick={() => navigate(isCourse ? "/campus" : "/my-deals")} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold h-11 rounded-full">
              {isCourse ? "Back to Campus" : "View my deals"}
            </Button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="h-14 w-14 text-amber-400 mx-auto" />
            <h1 className="text-xl font-bold text-white">
              {status === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-slate-400 text-sm">
              {status === "timeout"
                ? "Your payment is taking longer than expected. Check My Deals shortly."
                : "We couldn't confirm this payment."}
            </p>
            <Button onClick={() => navigate("/my-deals")} variant="outline" className="w-full h-11 rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10">
              Go to my deals
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
